/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const FullbleedContext = createContext<{
  fullbleed: boolean;
  acquireFullbleed: () => void;
  releaseFullbleed: () => void;
  flush: boolean;
  acquireFlush: () => void;
  releaseFlush: () => void;
}>({
  fullbleed: false,
  acquireFullbleed: () => {},
  releaseFullbleed: () => {},
  flush: false,
  acquireFlush: () => {},
  releaseFlush: () => {},
});

export function FullbleedProvider({ children }: { children: ReactNode }) {
  const [fullbleedCount, setFullbleedCount] = useState(0);
  const acquireFullbleed = useCallback(() => {
    setFullbleedCount((count) => count + 1);
  }, []);
  const releaseFullbleed = useCallback(() => {
    setFullbleedCount((count) => Math.max(0, count - 1));
  }, []);

  const [flushCount, setFlushCount] = useState(0);
  const acquireFlush = useCallback(() => {
    setFlushCount((count) => count + 1);
  }, []);
  const releaseFlush = useCallback(() => {
    setFlushCount((count) => Math.max(0, count - 1));
  }, []);

  return (
    <FullbleedContext.Provider
      value={{
        fullbleed: fullbleedCount > 0,
        acquireFullbleed,
        releaseFullbleed,
        flush: flushCount > 0,
        acquireFlush,
        releaseFlush,
      }}
    >
      {children}
    </FullbleedContext.Provider>
  );
}

export function useFullbleed() {
  return useContext(FullbleedContext);
}

/** Mount this component to enable fullbleed mode while the page is active. */
export function Fullbleed() {
  const { acquireFullbleed, releaseFullbleed } = useFullbleed();
  useEffect(() => {
    acquireFullbleed();
    return releaseFullbleed;
  }, [acquireFullbleed, releaseFullbleed]);
  return null;
}

/**
 * Keeps the dashboard's content card — its margin, border and rounding — but
 * drops the padding inside it, so a page can run its own rules, headers and
 * side columns to the card's edge. Fullbleed removes the card entirely; this
 * only clears the inset.
 */
export function Flush() {
  const { acquireFlush, releaseFlush } = useFullbleed();
  useEffect(() => {
    acquireFlush();
    return releaseFlush;
  }, [acquireFlush, releaseFlush]);
  return null;
}
